import React, { useState } from "react";
import { ArrowRight, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { COMPANY, DISCLAIMERS } from "@/lib/compliance";
import InquiryContactFields from "./inquiry/InquiryContactFields";
import InquiryHelpFields from "./inquiry/InquiryHelpFields";
import InquiryConsent from "./inquiry/InquiryConsent";
import InquirySuccess from "./inquiry/InquirySuccess";
import { submitInquiry, isValidEmail } from "./inquiry/submitInquiry";

const EMPTY = {
    full_name: "",
    phone: "",
    email: "",
    address: "",
    help_topic: "",
    help_note: "",
    e_consent: false,
};

function validate(values) {
    const e = {};
    if (!values.full_name.trim()) e.full_name = "Please enter your name.";
    if (!values.phone.trim() || values.phone.trim().length < 5)
        e.phone = "Please enter a valid phone number.";
    if (!values.email.trim()) e.email = "Email is required.";
    else if (!isValidEmail(values.email))
        e.email = "Please enter a valid email address.";
    if (!values.address.trim()) e.address = "Address is required.";
    if (!values.help_topic && !values.help_note.trim())
        e.help_topic = "Please tell us how we can help.";
    if (!values.e_consent)
        e.e_consent =
            "Please agree to electronic communications to continue.";
    return e;
}

/**
 * Main multi-field insurance inquiry form used on /contact and most CTA
 * landing slots. Submits through ``submitInquiry`` (the same pipeline
 * the ContactDialog uses) so leads always reach the agency mailbox no
 * matter which surface a visitor uses.
 *
 * Composition: contact fields → help fields → consent → submit. Each
 * section lives in its own file so this component stays under 200
 * lines and any future field-set can be added without touching the
 * submit/validate logic.
 */
export default function InquiryForm() {
    const [values, setValues] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [submitError, setSubmitError] = useState("");

    const update = (k, v) => {
        setValues((s) => ({ ...s, [k]: v }));
        if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (submitting) return;
        setSubmitError("");
        const v = validate(values);
        if (Object.keys(v).length > 0) {
            setErrors(v);
            toast.error("Please fill in all required fields.");
            return;
        }
        setSubmitting(true);
        const helpWith = [values.help_topic, values.help_note.trim()]
            .filter(Boolean)
            .join(" — ");
        try {
            await submitInquiry({
                ...values,
                help_with: helpWith,
                source_url:
                    typeof window !== "undefined" ? window.location.href : "",
            });
            setSent(true);
        } catch {
            setSubmitError(
                "We couldn't submit your request right now. Please call us directly at " +
                    COMPANY.phone +
                    " — please do not email sensitive information."
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (sent) {
        return (
            <InquirySuccess
                onReset={() => {
                    setSent(false);
                    setSubmitError("");
                    setValues(EMPTY);
                }}
            />
        );
    }

    return (
        <form
            data-testid="inquiry-form"
            onSubmit={onSubmit}
            noValidate
            aria-labelledby="inquiry-form-heading"
            className="ph-no-capture relative overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[#0b0e13]/80 p-6 backdrop-blur-sm md:p-8"
            data-ph-no-capture="true"
        >
            <h2 id="inquiry-form-heading" className="sr-only">
                Insurance inquiry form
            </h2>

            <p
                data-testid="form-state-scope"
                className="mb-5 rounded-md border border-[rgba(212,175,55,0.18)] bg-[rgba(212,175,55,0.04)] p-3 text-xs leading-relaxed text-brand-muted"
            >
                {DISCLAIMERS.stateScope}
            </p>

            <InquiryContactFields
                values={values}
                errors={errors}
                update={update}
            />

            <InquiryHelpFields
                values={values}
                errors={errors}
                update={update}
            />

            <InquiryConsent
                checked={values.e_consent}
                onChange={(v) => update("e_consent", v)}
                error={errors.e_consent}
            />

            {submitError && (
                <div
                    data-testid="form-error-message"
                    role="alert"
                    aria-live="assertive"
                    className="mt-5 flex items-start gap-3 rounded-md border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200"
                >
                    <AlertTriangle
                        aria-hidden="true"
                        className="mt-0.5 h-5 w-5 shrink-0 text-red-300"
                    />
                    <p>{submitError}</p>
                </div>
            )}

            <button
                data-testid="form-submit-button"
                type="submit"
                disabled={submitting}
                className="enc-submit mt-6 flex w-full items-center justify-center gap-3 rounded-md py-4 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg"
            >
                {submitting ? "Sending…" : "Send My Request"}
                <ArrowRight aria-hidden="true" className="h-4 w-4" />
            </button>

            <div className="mt-5 flex items-center justify-center gap-2 text-xs text-brand-muted">
                <ShieldCheck
                    aria-hidden="true"
                    className="h-3.5 w-3.5 text-brand-goldSoft"
                />
                <span>
                    Sent securely to {COMPANY.email} — a licensed agent
                    replies personally.
                </span>
            </div>
        </form>
    );
}
